import React from 'react'
import { Helmet } from 'react-helmet-async'
import { Link } from 'react-router-dom'
import {
  Box,
  Typography,
  Card,
  CardContent,
  Alert,
  AlertTitle,
  Grid,
  Button,
} from '@mui/material'
import type { AlertProps, SvgIconProps } from '@mui/material'
import AdminPageHeader from './shared/AdminPageHeader'
import type { AdminPageKey, BreadcrumbItem } from '../../config/adminPages'

const TEAL = '#007f98'
const TEAL_HOVER = '#005f73'

export interface AdminLandingCard {
  icon: React.ComponentType<SvgIconProps>
  title: string
  description: string
  to?: string
  buttonLabel: string
  disabled?: boolean
}

export interface AdminLandingAlert {
  severity: AlertProps['severity']
  title?: string
  content: React.ReactNode
}

export interface AdminLandingPageProps {
  /** When set, the standard AdminPageHeader (registry icon/title/description +
   *  breadcrumbs) is rendered. Falls back to the legacy header otherwise. */
  pageKey?: AdminPageKey
  /** Sub-page title/description/trail when this landing reuses a parent's
   *  pageKey (e.g. Sync Tools under the dataSync key). Forwarded to AdminPageHeader. */
  titleOverride?: string
  descriptionOverride?: string
  breadcrumbs?: BreadcrumbItem[]
  documentTitle?: string
  title?: string
  description?: string
  alert?: AdminLandingAlert
  cards: AdminLandingCard[]
}

const AdminLandingPage: React.FC<AdminLandingPageProps> = ({
  pageKey,
  titleOverride,
  descriptionOverride,
  breadcrumbs,
  documentTitle,
  title,
  description,
  alert,
  cards,
}) => {
  return (
    <>
      {pageKey ? (
        <AdminPageHeader
          pageKey={pageKey}
          titleOverride={titleOverride}
          descriptionOverride={descriptionOverride}
          breadcrumbs={breadcrumbs}
        />
      ) : (
        <>
          <Helmet>
            <title>{documentTitle} - Inspector Tool</title>
          </Helmet>

          <Box sx={{ mb: 3 }}>
            <Typography variant="h4" component="h1" gutterBottom>
              {title}
            </Typography>
            <Typography variant="body1" color="textSecondary">
              {description}
            </Typography>
          </Box>
        </>
      )}

      {alert && (
        <Alert severity={alert.severity} sx={{ mb: 3 }}>
          {alert.title && <AlertTitle>{alert.title}</AlertTitle>}
          {alert.content}
        </Alert>
      )}

      <Grid container spacing={3}>
        {cards.map((card) => {
          const Icon = card.icon
          const isDisabled = card.disabled || !card.to
          return (
            <Grid key={card.title} item xs={12} md={6} lg={4}>
              <Card sx={card.disabled ? { opacity: 0.6 } : undefined}>
                <CardContent>
                  <Box sx={{ textAlign: 'center', py: 3 }}>
                    <Icon
                      sx={{
                        fontSize: 48,
                        color: card.disabled ? 'text.secondary' : TEAL,
                        mb: 2,
                      }}
                    />
                    <Typography variant="h6" gutterBottom>
                      {card.title}
                    </Typography>
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                      {card.description}
                    </Typography>
                    {isDisabled ? (
                      <Button
                        variant="outlined"
                        disabled
                        sx={{ color: 'text.secondary', borderColor: 'text.secondary' }}
                      >
                        {card.buttonLabel}
                      </Button>
                    ) : (
                      <Button
                        component={Link}
                        to={card.to!}
                        variant="contained"
                        sx={{
                          backgroundColor: TEAL,
                          '&:hover': { backgroundColor: TEAL_HOVER },
                        }}
                      >
                        {card.buttonLabel}
                      </Button>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          )
        })}
      </Grid>
    </>
  )
}

export default AdminLandingPage
